import React, { useEffect, useState, useCallback } from 'react';
import { Card, Grid, Header, Progress, Segment, Label, Statistic, Dropdown, Button, Icon, Loader, Dimmer } from 'semantic-ui-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { API, showError } from '../../helpers';
import './GPU.css';

const GPU_POLL_INTERVAL = 10000; // 10s

const statusColors = {
  low: 'green',
  medium: 'yellow',
  high: 'orange',
  critical: 'red',
};

function getUtilLevel(util) {
  if (util < 30) return 'low';
  if (util < 60) return 'medium';
  if (util < 85) return 'high';
  return 'critical';
}

function getMemLevel(used, total) {
  const pct = total > 0 ? (used / total) * 100 : 0;
  if (pct < 50) return 'low';
  if (pct < 75) return 'medium';
  if (pct < 90) return 'high';
  return 'critical';
}

function formatMB(mb) {
  if (mb >= 1024) return (mb / 1024).toFixed(1) + ' GB';
  return mb + ' MB';
}

function formatTime(ts) {
  const d = new Date(ts * 1000);
  return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
}

const GPUCard = ({ gpu, onShowHistory }) => {
  const memPct = gpu.memory_total > 0 ? ((gpu.memory_used / gpu.memory_total) * 100).toFixed(1) : 0;
  const memLevel = getMemLevel(gpu.memory_used, gpu.memory_total);
  const utilLevel = getUtilLevel(gpu.utilization);

  return (
    <Card fluid>
      <Card.Content>
        <Card.Header>
          <Icon name='microchip' />
          GPU {gpu.index}
          <Label color={statusColors[utilLevel]} size='mini' style={{ marginLeft: 8 }}>
            {gpu.utilization}%
          </Label>
          {gpu.running_models && gpu.running_models.length > 0 && (
            <Label color='blue' size='mini' style={{ marginLeft: 4 }}>
              {gpu.running_models.length} 模型运行中
            </Label>
          )}
        </Card.Header>
        <Card.Meta>{gpu.name}</Card.Meta>
      </Card.Content>
      <Card.Content>
        <Grid columns={2} stackable>
          <Grid.Column>
            <div style={{ marginBottom: 8 }}>
              <strong>显存</strong>
              <span style={{ float: 'right' }}>{formatMB(gpu.memory_used)} / {formatMB(gpu.memory_total)}</span>
            </div>
            <Progress
              percent={parseFloat(memPct)}
              color={statusColors[memLevel]}
              size='small'
              progress
            />
          </Grid.Column>
          <Grid.Column>
            <div style={{ marginBottom: 8 }}>
              <strong>利用率</strong>
              <span style={{ float: 'right' }}>{gpu.utilization}%</span>
            </div>
            <Progress
              percent={gpu.utilization}
              color={statusColors[utilLevel]}
              size='small'
              progress
            />
          </Grid.Column>
        </Grid>
        <Grid columns={3} style={{ marginTop: 8 }}>
          <Grid.Column textAlign='center'>
            <Statistic size='mini'>
              <Statistic.Value>{gpu.temperature}</Statistic.Value>
              <Statistic.Label>温度</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column textAlign='center'>
            <Statistic size='mini'>
              <Statistic.Value>{gpu.power_draw}W</Statistic.Value>
              <Statistic.Label>功耗</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column textAlign='center'>
            <Statistic size='mini'>
              <Statistic.Value>{gpu.fan_speed}%</Statistic.Value>
              <Statistic.Label>风扇</Statistic.Label>
            </Statistic>
          </Grid.Column>
        </Grid>
        {gpu.running_models && gpu.running_models.length > 0 && (
          <Segment basic style={{ padding: '8px 0 0 0' }}>
            <strong>运行中的模型:</strong>
            <div style={{ marginTop: 4 }}>
              {gpu.running_models.map((m) => (
                <Label key={m.id} size='small' style={{ margin: 2 }}>
                  {m.display_name || m.model_name}
                </Label>
              ))}
            </div>
          </Segment>
        )}
      </Card.Content>
      <Card.Content extra>
        <Button size='mini' basic onClick={() => onShowHistory(gpu.index)}>
          <Icon name='chart line' /> 历史趋势
        </Button>
      </Card.Content>
    </Card>
  );
};

const GPUHistoryChart = ({ gpuIndex, nodeId }) => {
  const [data, setData] = useState([]);
  const [hours, setHours] = useState(6);
  const [loading, setLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`/api/gpu/history/${gpuIndex}?hours=${hours}&node_id=${nodeId}`);
      const { success, data: histData } = res.data;
      if (success && histData) {
        setData(histData.map(s => ({
          time: formatTime(s.created_time),
          memory_used: s.memory_used,
          utilization: s.utilization,
          temperature: s.temperature,
          power_draw: s.power_draw,
        })));
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  }, [gpuIndex, hours, nodeId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const hoursOptions = [
    { key: 1, text: '1小时', value: 1 },
    { key: 6, text: '6小时', value: 6 },
    { key: 24, text: '24小时', value: 24 },
    { key: 72, text: '3天', value: 72 },
    { key: 168, text: '7天', value: 168 },
  ];

  return (
    <Segment>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Header as='h4'>GPU {gpuIndex} 历史趋势</Header>
        <Dropdown
          selection compact
          options={hoursOptions}
          value={hours}
          onChange={(e, { value }) => setHours(value)}
        />
      </div>
      {loading ? (
        <Dimmer active inverted><Loader /></Dimmer>
      ) : (
        <Grid columns={2} stackable>
          <Grid.Column>
            <Header as='h5' textAlign='center'>显存 (MB)</Header>
            <ResponsiveContainer width='100%' height={200}>
              <AreaChart data={data}>
                <CartesianGrid strokeDasharray='3 3' opacity={0.1} />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type='monotone' dataKey='memory_used' stroke='#4318FF' fill='#4318FF' fillOpacity={0.15} />
              </AreaChart>
            </ResponsiveContainer>
          </Grid.Column>
          <Grid.Column>
            <Header as='h5' textAlign='center'>利用率 (%)</Header>
            <ResponsiveContainer width='100%' height={200}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray='3 3' opacity={0.1} />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} padding={{ bottom: 10 }} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type='monotone' dataKey='utilization' stroke='#00B5D8' dot={false} strokeWidth={1.5} />
              </LineChart>
            </ResponsiveContainer>
          </Grid.Column>
          <Grid.Column>
            <Header as='h5' textAlign='center'>温度</Header>
            <ResponsiveContainer width='100%' height={200}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray='3 3' opacity={0.1} />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type='monotone' dataKey='temperature' stroke='#FF6B6B' dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Grid.Column>
          <Grid.Column>
            <Header as='h5' textAlign='center'>功耗 (W)</Header>
            <ResponsiveContainer width='100%' height={200}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray='3 3' opacity={0.1} />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type='monotone' dataKey='power_draw' stroke='#FFA500' dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Grid.Column>
        </Grid>
      )}
    </Segment>
  );
};

const GPU = () => {
  const [gpus, setGpus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGPU, setSelectedGPU] = useState(null);
  const [nodeId] = useState(1);

  const loadGPUStatus = useCallback(async () => {
    try {
      const res = await API.get(`/api/gpu/status/detail?node_id=${nodeId}`);
      const { success, data } = res.data;
      if (success && data) {
        setGpus(data);
      }
    } catch (e) {
      // Silently fail for polling
    }
    setLoading(false);
  }, [nodeId]);

  useEffect(() => {
    loadGPUStatus();
    const timer = setInterval(loadGPUStatus, GPU_POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [loadGPUStatus]);

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            <Icon name='server' /> GPU 监控
            <Button floated='right' size='mini' icon='refresh' onClick={loadGPUStatus} />
          </Card.Header>
        </Card.Content>
        <Card.Content>
          {loading ? (
            <Dimmer active inverted><Loader content='加载 GPU 数据中...' /></Dimmer>
          ) : gpus.length === 0 ? (
            <Segment placeholder textAlign='center'>
              <Header icon>
                <Icon name='warning sign' />
                未检测到 GPU 数据
              </Header>
              <p>请确保已设置环境变量 ENABLE_GPU_MONITOR=true 并且服务器安装了 nvidia-smi</p>
            </Segment>
          ) : (
            <Grid columns={gpus.length <= 3 ? gpus.length : 3} stackable>
              {gpus.map((gpu) => (
                <Grid.Column key={gpu.index}>
                  <GPUCard
                    gpu={gpu}
                    onShowHistory={(idx) => setSelectedGPU(selectedGPU === idx ? null : idx)}
                  />
                </Grid.Column>
              ))}
            </Grid>
          )}
        </Card.Content>
      </Card>
      {selectedGPU !== null && (
        <Card fluid className='chart-card' style={{ marginTop: 16 }}>
          <Card.Content>
            <GPUHistoryChart gpuIndex={selectedGPU} nodeId={nodeId} />
          </Card.Content>
        </Card>
      )}
    </div>
  );
};

export default GPU;
